var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import QRCode from 'qrcode.react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { useApiAbort } from '../../Api/Core/useApiAbort';
import { MakhazinApi } from '../../Api/MakhazinApi';
import ActionBar from '../../Components/ActionBar';
import PersonalIdentificationField from '../../Components/Field/PersonalIdentificationField';
import { useUserContext } from '../../Context/UserContext';
import { useStyles } from './LoginForm.styles';
const isMobileDevice = () => {
    return /Mobi/i.test(navigator.userAgent) || /Android/i.test(navigator.userAgent);
};
function iOS() {
    var iDevices = ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'];
    if (!!navigator.platform) {
        while (iDevices.length) {
            if (navigator.platform === iDevices.pop()) {
                return true;
            }
        }
    }
    return false;
}
const LoginProcess = (props) => {
    const abortController = useApiAbort();
    const [external, setExternal] = useState(false);
    const [internal, setInternal] = useState(false);
    const [message, setMessage] = useState(undefined);
    const [cancelling, setCancelling] = useState(false);
    const [qrToken, setQrToken] = useState(props.qrToken);
    const { orderRef, invitationToken, onAuthenticated, onFailed, retryLogin } = props;
    const collectRevision = useRef(0);
    const failed = useRef(false);
    useEffect(() => {
        failed.current = false;
    }, [orderRef, props.startToken]);
    useEffect(() => {
        const startAnimatedQr = () => __awaiter(void 0, void 0, void 0, function* () {
            const result = yield new MakhazinApi(abortController.current).generateQrToken(props.startToken);
            setQrToken(result.qrData);
        });
        collectRevision.current = collectRevision.current + 1;
        const collect = (revision) => __awaiter(void 0, void 0, void 0, function* () {
            if (revision !== collectRevision.current) {
                return;
            }
            try {
                const result = yield new MakhazinApi(abortController.current).collect(orderRef, !external, isMobileDevice(), invitationToken);
                setMessage(result.message);
                if (external && result.hintCode === 'startFailed') {
                    failed.current = true;
                    setTimeout(retryLogin, 2000);
                }
                else if (result.status === 'pending') {
                    setTimeout(() => collect(revision), 1000);
                    setTimeout(startAnimatedQr, 1000);
                }
                else if (result.status === 'complete') {
                    onAuthenticated(result.user, result.user.accessToken, result.user.refreshToken);
                }
                else {
                    onFailed(result.message);
                }
            }
            catch (exception) {
                if (!abortController.current.signal.aborted) {
                    onFailed('Något gick fel');
                }
            }
        });
        if (!failed.current) {
            collect(collectRevision.current);
        }
    }, [abortController, external, invitationToken, onAuthenticated, onFailed, orderRef, props.startToken, retryLogin]);
    const handleOpenBankId = () => {
        props.activeBankID(true);
        setInternal(true);
        const redirect = `&redirect=null`;
        const url = `bankid:///?autostarttoken=${props.autoStartToken}${redirect}`;
        const iosUrl = `https://app.bankid.com/?autostarttoken=${props.autoStartToken}${redirect}`;
        window.location.href = iOS() ? iosUrl : url;
    };
    const handleOpenExternalBankId = () => {
        props.activeBankIDExternal(true);
        setExternal(true);
    };
    const handleCancel = () => __awaiter(void 0, void 0, void 0, function* () {
        props.activeBankIDExternal(false);
        props.activeBankID(false);
        setCancelling(true);
        try {
            yield new MakhazinApi(abortController.current).cancel(props.orderRef);
        }
        catch (exception) { }
    });
    const classes = useStyles();
    return (React.createElement(Box, { component: "div", sx: classes.signInContainer }, (!external && !internal && (React.createElement(React.Fragment, null,
        React.createElement(Button, { onClick: handleOpenExternalBankId, color: "primary", size: "large" }, "\u00D6ppna BankID-appen p\u00E5 en mobil enhet"),
        React.createElement(ActionBar, { customText: "\u00D6ppna BankID", navigateForward: handleOpenBankId, navigateBack: handleCancel, customIcon: !cancelling ? (React.createElement(React.Fragment, null,
                React.createElement(CancelIcon, { sx: classes.cancelIcon, color: "error" }),
                React.createElement(Box, { component: "span", sx: classes.cancelText }, "Avbryt"))) : (React.createElement(CircularProgress, null)) })))) ||
        (external && (React.createElement(Grid, { item: true, sx: classes.messageContainer },
            React.createElement(Box, { component: "div", sx: classes.qrCode },
                React.createElement(QRCode, { value: qrToken })),
            message && React.createElement(Typography, { sx: classes.message }, message),
            React.createElement(Typography, { sx: classes.startBankId }, "Starta Mobilt BankID. Klicka p\u00E5 QR-ikonen i din BankID-app och rikta sedan kameran mot QR-koden som visas h\u00E4r p\u00E5 sk\u00E4rmen.")))) ||
        (internal && (React.createElement(React.Fragment, null,
            message && React.createElement(Typography, null, message),
            React.createElement(Box, { component: "div", sx: classes.progress },
                React.createElement(CircularProgress, null)),
            React.createElement(Button, { onClick: handleCancel, sx: classes.cancel }, "Avbryt"))))));
};
export const LoginForm = (props) => {
    const classes = useStyles();
    const abortController = useApiAbort();
    const [personalNumber, setPersonalNumber] = useState(undefined);
    const [validForm, setValidForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loginFailedMessage, setLoginFailedMessage] = useState(undefined);
    const [autoStartToken, setAutoStartToken] = useState(undefined);
    const [orderRef, setOrderRef] = useState(undefined);
    const loginUser = useUserContext().loginUser;
    const user = useUserContext().user;
    const navivate = useNavigate();
    const [startToken, setStartToken] = useState(undefined);
    const [requestStartTime, setRequestStartTime] = useState(undefined);
    const [qrToken, setQrToken] = useState(undefined);
    const { invitationToken, reservationId } = useParams();
    useEffect(() => {
        if (personalNumber) {
            setValidForm(true);
        }
        else {
            setValidForm(false);
        }
    }, [personalNumber]);
    const handleComplete = (user, accessToken, refreshToken) => {
        const route = loginUser(user, accessToken, refreshToken);
        navivate(route);
    };
    const handlePersonalIdentificationChanged = (id) => {
        if (loginFailedMessage && id !== personalNumber) {
            props.errorMessage('');
            setLoginFailedMessage(undefined);
        }
        setPersonalNumber(id);
    };
    const handleLogin = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (personalNumber) {
            setLoading(true);
            try {
                setLoginFailedMessage(undefined);
                var result = yield new MakhazinApi(abortController.current).auth(personalNumber, reservationId, invitationToken);
                if (result.message) {
                    setLoginFailedMessage(result.message);
                    props.errorMessage(result.message);
                    setValidForm(false);
                }
                else {
                    setOrderRef(result.orderRef);
                    setQrToken(result.qrData);
                    setStartToken(result.startToken);
                    setRequestStartTime(new Date());
                    setAutoStartToken(result.autoStartToken);
                }
            }
            catch (exception) {
                if (!abortController.current.signal.aborted) {
                    setLoginFailedMessage('Någonting gick fel');
                    props.errorMessage('Någonting gick fel');
                }
            }
            setLoading(false);
        }
    }), [abortController, invitationToken, personalNumber, props, reservationId]);
    const handleFormSubmit = (event) => {
        event.preventDefault();
        handleLogin();
        return false;
    };
    return (React.createElement(Box, { component: "section", sx: classes.root }, (user && React.createElement(Navigate, { to: "/" })) || (React.createElement(React.Fragment, null, (orderRef === undefined && (React.createElement(React.Fragment, null,
        React.createElement("form", { onSubmit: handleFormSubmit },
            React.createElement(Grid, { container: true, direction: "column", alignItems: "center", sx: classes.inputWrapper },
                React.createElement(PersonalIdentificationField, { sx: classes.input, onChange: handlePersonalIdentificationChanged }),
                (!loading && (React.createElement(ActionBar, { customText: "Logga in", navigateForward: handleLogin, disabled: !validForm }))) || React.createElement(CircularProgress, null)))))) ||
        (autoStartToken !== undefined && orderRef !== undefined && (React.createElement(LoginProcess, { invitationToken: invitationToken, startToken: startToken, qrToken: qrToken, requestStartTime: requestStartTime, autoStartToken: autoStartToken, orderRef: orderRef, onFailed: (message) => {
                setAutoStartToken(undefined);
                setOrderRef(undefined);
                setLoginFailedMessage(message);
            }, activeBankID: props.activeBankID, activeBankIDExternal: props.activeBankIDExternal, onAuthenticated: handleComplete, retryLogin: handleLogin })))))));
};
export default LoginForm;
